import { FormattedMessage, useIntl } from 'react-intl'
import { useRouter } from 'next/router'
import * as Layout from 'layouts/pages'
import * as Index from './'
import Head from 'components/Head'
import { List, Entry } from 'components/description'
import * as Config from 'clients/config'
import * as Texts from 'components/text'
import Client from 'clients/incidents'
import * as NextUtils from 'utils/next'
import styles from './incidents.module.css'
import { isNotFound } from '@avcan/utils/fetch'

export async function getStaticProps({ params }) {
    try {
        const incident = await Client.getByID(params.id)

        return {
            props: {
                incident,
            },
            revalidate: 300, // in seconds, 5 minutes
        }
    } catch (error) {
        if (isNotFound(error)) {
            return {
                notFound: true,
            }
        }
    }
}

export async function getStaticPaths() {
    const incidents = await Client.get() // First page only
    const paths = incidents.results.map(({ id }) => ({ params: { id } }))

    return {
        paths: NextUtils.internationalizeStaticPaths(paths),
        fallback: true,
    }
}

export default function IncidentDetails({ incident }) {
    const router = useRouter()

    if (router.isFallback) {
        return <Layout.Loading></Layout.Loading>
    }

    return (
        <article>
            <Head title={incident.location} description={incident.location} />
            <Summary incident={incident} />
            <Avalanche avalanches={incident.avalanche_obs} />
            <Weather observations={incident.weather_obs} comment={incident.weather_comment} />
            <Snowpack observations={incident.snowpack_obs} comment={incident.snowpack_comment} />
            <Documents docs={incident.documents} />
        </article>
    )
}

IncidentDetails.getLayout = Index.getLayout

function Summary({ incident }) {
    return (
        <section className={styles.Summary}>
            <h2>
                <FormattedMessage
                    description="Layout incidents/layouts"
                    defaultMessage="Incident Summary"
                />
            </h2>
            <List>
                <Entry term="Date">{incident.ob_date}</Entry>
                <Entry term="Location">{incident.location}</Entry>
                <Entry term="Location Description">{incident.location_desc}</Entry>
                <Entry term="Province">{incident.location_province}</Entry>
                <Entry term="Coordinates">{Index.latLng(incident.location_coords)}</Entry>
                <Entry term="Elevation">{incident.location_elevation} m</Entry>
                <Entry term="Activity">{incident.group_activity}</Entry>
                <Entry term="Involvement">{incident.num_involved}</Entry>
                <Entry term="Injury">{incident.num_injured}</Entry>
                <Entry term="Fatality">{incident.num_fatal}</Entry>
                <Entry term="Description">{incident.comment}</Entry>
            </List>
        </section>
    )
}

function Avalanche({ avalanches }) {
    const rows = avalanches.map((avalanche, i) => (
        <tr key={i}>
            <td>{avalanche.observation_date}</td>
            <Index.Cell>{avalanche.size}</Index.Cell>
            <Index.Cell>{avalanche.type}</Index.Cell>
            <Index.Cell>{avalanche.trigger}</Index.Cell>
            <Index.Cell suffix="m">{avalanche.elevation}</Index.Cell>
            <Index.Cell>{avalanche.aspect}</Index.Cell>
            <Index.Cell suffix="m">{avalanche.slab_width}</Index.Cell>
            <Index.Cell suffix="cm">{avalanche.slab_thickness}</Index.Cell>
        </tr>
    ))

    return (
        <Section title="Avalanches">
            <table>
                <thead>
                    <tr>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Date/Time"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Size"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Type"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Trigger"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Elevation"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Aspect"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Slab width"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Slab Thickness"
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>{rows}</tbody>
            </table>
        </Section>
    )
}

function Weather({ observations, comment }) {
    const intl = useIntl()

    return (
        <Section title="Weather">
            <table>
                <thead>
                    <tr>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Present Temp"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Max Temp"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Min Temp"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="24hr Trend"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Wind Speed"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Wind Direction"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Sky Condition"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Precipitation Type & Intensity"
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <Index.Cell>{observations.temp_pressent}</Index.Cell>
                        <Index.Cell>{observations.temp_max}</Index.Cell>
                        <Index.Cell>{observations.temp_min}</Index.Cell>
                        <Index.Cell>{observations.temp_trend}</Index.Cell>
                        <Index.Cell>{observations.wind_speed}</Index.Cell>
                        <Index.Cell>{observations.wind_dir}</Index.Cell>
                        <Index.Cell>{observations.sky}</Index.Cell>
                        <Index.Cell>{observations.precip}</Index.Cell>
                    </tr>
                </tbody>
            </table>
            <Comment
                title={intl.formatMessage({
                    defaultMessage: 'Weather Comment',
                    description: 'Layout incidents/layouts',
                })}>
                {comment}
            </Comment>
        </Section>
    )
}

function Snowpack({ observations, comment }) {
    const intl = useIntl()
    return (
        <Section title="Snowpack">
            <table>
                <thead>
                    <tr>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Snowpack"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="24hr Snow"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Storm Snow"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layouts"
                                defaultMessage="Storm Date"
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <Index.Cell suffix="cm">{observations.hs}</Index.Cell>
                        <Index.Cell suffix="cm">{observations.hn24}</Index.Cell>
                        <Index.Cell suffix="cm">{observations.hst}</Index.Cell>
                        <Index.Cell suffix="cm">{observations.hst_reset}</Index.Cell>
                    </tr>
                </tbody>
            </table>
            <Comment
                title={intl.formatMessage({
                    defaultMessage: 'Snowpack Comment',
                    description: 'Layout incidents/layouts',
                })}>
                {comment}
            </Comment>
        </Section>
    )
}

function Documents({ docs }) {
    const rows = docs.map((d, i) => (
        <tr key={i}>
            <td>{d.date}</td>
            <td>{d.title}</td>
            <td>{d.source}</td>
            <td>
                <a href={Config.incidents + d.url} target={d.title}>
                    <FormattedMessage description="Layout incidents/layout" defaultMessage="view" />
                </a>
            </td>
        </tr>
    ))

    return (
        <Section title="Documents">
            {rows.length === 0 ? (
                <Texts.Muted>
                    <FormattedMessage
                        description="Layout incidents/layouts"
                        defaultMessage="No documents available."
                    />
                </Texts.Muted>
            ) : (
                <table>
                    <thead>
                        <tr>
                            <th>
                                <FormattedMessage
                                    description="Layout incidents/layouts"
                                    defaultMessage="Document Date"
                                />
                            </th>
                            <th>
                                <FormattedMessage
                                    description="Layout incidents/layouts"
                                    defaultMessage="Title"
                                />
                            </th>
                            <th>
                                <FormattedMessage
                                    description="Layout incidents/layouts"
                                    defaultMessage="Source"
                                />
                            </th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
            )}
        </Section>
    )
}

function Section({ title, children }) {
    return (
        <section>
            <h3>{title}</h3>
            {children}
        </section>
    )
}

function Comment({ children, title }) {
    return children ? (
        <p>
            <b>{title}: </b> {children}
        </p>
    ) : null
}
